import * as React from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, SlidersHorizontalIcon } from "lucide-react";

import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";

export type DataTableColumnConfigLabels = {
  /** Trigger button label. */
  trigger: string;
  /** Panel heading. */
  title: string;
  /** Heading of the reorder section. */
  orderSection: string;
  /** Reset-to-default action. */
  reset: string;
  /** Shown when there are no reorderable columns. */
  empty: string;
  /** Accessible label on a row's drag handle. */
  dragHandle: string;
};

const DEFAULT_LABELS: DataTableColumnConfigLabels = {
  trigger: "Columns",
  title: "Table columns",
  orderSection: "Order",
  reset: "Reset",
  empty: "No reorderable columns",
  dragHandle: "Drag to reorder",
};

export type DataTableColumnConfigColumn = {
  id: string;
  label: string;
};

export type DataTableColumnConfigProps = {
  /** Reorderable columns, in their current order. */
  columns: DataTableColumnConfigColumn[];
  /** Called with the new order of {@link columns} (the column ids) after a drag. */
  onReorder: (orderedIds: string[]) => void;
  /** Optional reset-to-default-order action; hidden when omitted. */
  onReset?: () => void;
  labels?: Partial<DataTableColumnConfigLabels>;
  /**
   * Extra config sections rendered below the column-order section. The panel is intentionally a sectioned shell so
   * future config (visibility, pinning, density, …) slots in here without reworking the trigger/layout.
   */
  children?: React.ReactNode;
};

function SortableColumnRow({ id, label, dragHandleLabel }: { id: string; label: string; dragHandleLabel: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "border-border/60 bg-background flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm",
        isDragging && "z-10 opacity-70 shadow-sm",
      )}
    >
      <button
        type='button'
        aria-label={dragHandleLabel}
        className='text-muted-foreground hover:text-foreground -ml-0.5 cursor-grab touch-none active:cursor-grabbing'
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon className='size-4' />
      </button>
      <span className='min-w-0 truncate'>{label}</span>
    </li>
  );
}

/**
 * A column-configuration menu: a popover with a drag-to-reorder (dnd-kit) list of columns. Built as a sectioned
 * shell so more config can be added later (see {@link DataTableColumnConfigProps.children}). Pure UI — the caller
 * owns the table and applies the new order via {@link DataTableColumnConfigProps.onReorder}.
 */
export function DataTableColumnConfig({
  columns,
  onReorder,
  onReset,
  labels,
  children,
}: DataTableColumnConfigProps) {
  const resolved = React.useMemo<DataTableColumnConfigLabels>(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = React.useMemo(() => columns.map((column) => column.id), [columns]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type='button' variant='ghost' size='sm' className='text-muted-foreground gap-1.5'>
          <SlidersHorizontalIcon className='size-4' />
          {resolved.trigger}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-72'>
        <div className='space-y-3'>
          <div className='flex items-center justify-between gap-2'>
            <p className='text-sm font-medium'>{resolved.title}</p>
            {onReset ? (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='text-muted-foreground h-auto px-1.5 py-0.5 text-xs'
                onClick={onReset}
              >
                {resolved.reset}
              </Button>
            ) : null}
          </div>

          <div className='space-y-1.5'>
            <p className='text-muted-foreground text-xs font-medium uppercase tracking-wide'>{resolved.orderSection}</p>
            {columns.length === 0 ? (
              <p className='text-muted-foreground text-xs'>{resolved.empty}</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                  <ul className='space-y-1'>
                    {columns.map((column) => (
                      <SortableColumnRow
                        key={column.id}
                        id={column.id}
                        label={column.label}
                        dragHandleLabel={resolved.dragHandle}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}
